import React, { useState, useRef, useEffect } from 'react';
import { Chip, Grid, Typography, Box, Button } from '@mui/material';
import { makeStyles, useTheme } from '@mui/styles';
import { Add } from '@mui/icons-material';
import SmartTooltipChip from '../../../components/SmartTooltipChip';

import AddPatientToListDialog from '../../../components/AddPatientToListDialog/AddPatientToListDialog';

const useStyles = makeStyles((theme) => ({
  patientChartCard: {
    ...theme.globalClasses.patientChartCard,
    ...theme.globalClasses.adminTableWrapper,
    paddingTop: 0,
    paddingBottom: 0,
    position: 'relative',
  },
  container: {
    maxHeight: 'auto',
    overflowY: 'visible',
    position: 'relative',
    width: '100%',
  },
  addList: {
    fontSize: 18,
    marginBottom: -5,
    fontWeight: 'bold',
  },
  chipContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    minHeight: '36px',
    flexWrap: 'wrap',
  },
  chipGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 135px))',
    gap: '8px',
    alignItems: 'flex-start',
  },
  additionalChipsContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    marginTop: '8px',
    position: 'absolute',
    top: '100%',
    left: 0,
    zIndex: 10,
    backgroundColor: '#fff',
    padding: '8px 0',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-start',
    marginRight: 'auto',
  },
}));

const PatientAddToListBox = ({ data, goListTab, currentTab, refetch }) => {
  const classes = useStyles();
  const theme = useTheme();
  const [addingPatient, setAddingPatient] = useState(false);
  const [showAllChips, setShowAllChips] = useState(false);

  // Filter out invalid data items (missing or empty list_name)
  const validData = Array.isArray(data)
    ? data.filter((item) => item && item.list_name && item.list_name.trim() !== '')
    : [];

  // Check if valid data exists
  const hasData = validData.length > 0;

  // Get initial and additional chips
  const initialChips = hasData ? validData.slice(0, 3) : [];
  const additionalChips = hasData ? validData.slice(3) : [];

  const renderChip = (item, index) => (
    <SmartTooltipChip
      key={index}
      label={item.list_name}
      clickable={false}
      color="default"
      sx={{
        height: '32px',
        maxWidth: '135px',
        backgroundColor: theme.palette.grey[300],
        cursor: 'default',
        '&:hover': {
          backgroundColor: `${theme.palette.grey[300]} !important`,
        },
        '&:active': {
          backgroundColor: `${theme.palette.grey[300]} !important`,
        },
      }}
    />
  );

  return (
    <Grid
      item
      xs={12}
      className={classes.patientChartCard}
    >
      <Box
        className={classes.container}
        sx={{
          display: 'grid',
          gridTemplateColumns: '190px 1fr',
          gap: '16px',
          position: 'relative',
          width: '100%',
          alignItems: 'center',
        }}
      >
        {/* Button column - vertically centered */}
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            variant="outlined"
            size="large"
            sx={{
              borderColor: 'rgba(39, 24, 105, 0.50)',
              color: theme.palette.admin.primaryLight,
              borderRadius: '8px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              whiteSpace: 'nowrap',
              width: '190px',
              padding: '8px 22px',
              minWidth: '190px',
              '&:hover': {
                borderColor: theme.palette.admin.main,
                color: theme.palette.admin.primaryLight,
              },
            }}
            startIcon={<Add style={{ position: 'relative' }} />}
            onClick={() => setAddingPatient(true)}
          >
            Add To List
          </Button>
        </Box>

        {/* Chips column with its dropdown - vertically centered */}
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          {/* Visible chips */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
            }}
          >
            {hasData && initialChips.map((item, index) => renderChip(item, `initial-${index}`))}

            {hasData && validData.length > 3 && (
              <Chip
                label={showAllChips ? `-${validData.length - 3}` : `+${validData.length - 3}`}
                clickable={true}
                onClick={() => setShowAllChips(!showAllChips)}
                sx={{
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              />
            )}
          </Box>

          {/* Dropdown containing additional chips */}
          {hasData && showAllChips && (
            <Box className={classes.additionalChipsContainer}>
              <Box className={classes.chipGrid}>
                {additionalChips.map((item, index) => renderChip(item, `additional-${index}`))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {currentTab && refetch && (
        <AddPatientToListDialog
          open={addingPatient}
          patient={currentTab}
          handleClose={() => setAddingPatient(false)}
          refetch={refetch}
        />
      )}
    </Grid>
  );
};

export default PatientAddToListBox;
